import { Dispatch, SetStateAction } from "react";
import { DateTime } from "luxon";
import { RemoteCateringMenu } from "../../../../ServiceManagement/Catering/Domain/Types/Remote-CateringMenu.types";
import { ServiceData } from "../ScheduleSelection-Tab-Services.component";
import {
  ServicesZoneBooking,
  ServiceType
} from "../../../../../features/Booking-Form/typings/booking-service-administration.types";
import { IEquipmentInventory } from "../../../../FacilityManager/Domain/Types/FloorPlan/Equipment.type";
import {
  RemoteRoomlayout,
  RoomlayoutIds
} from "../../../../FacilityManager/Domain/Types/FloorPlan/RoomlayoutAssignment.type";
import { IFloorPayload } from "../../../../FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import { CateringMenuIds } from "../../../../FacilityManager/Domain/Types/FloorPlan/CateringMenuAssignment.type";
import { BookableEquipmentIds } from "../../../../FacilityManager/Domain/Types/FloorPlan/BookableEquipmentAssignment.type";
import { isServiceItemOrderable } from "../../../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/functions/filterByServiceTime.function";
import { checkPermission } from "../../../../FacilityManager/Components/Views/SelectFloorPlanView/BookingServicePrompt/functions/handleAssignedServiceItems.function";
import { PermissionContext } from "../../../../../hooks/Remote/User/UserInfo/useRemoteFetchPermissionContext";

export const filterOrderByType = (serviceBookings: ServicesZoneBooking[]) => {
  const orderedCatering = serviceBookings.filter(book => book.serviceType === ServiceType.Catering);
  const orderedBookEquip = serviceBookings.filter(
    book => book.serviceType === ServiceType.Bookable_Equipment
  );
  const orderedRoomLayout = serviceBookings.filter(
    book => book.serviceType === ServiceType.Room_Layout
  );

  return { orderedCatering, orderedBookEquip, orderedRoomLayout };
};

/**
 * find the assigned service items
 */
export const filterAssignedService = ({
  floorPlanForService,
  bookedEntryId,
  orderedCatering,
  orderedBookEquip,
  orderedRoomLayout
}: {
  floorPlanForService: IFloorPayload;
  bookedEntryId: number;
  orderedCatering: ServicesZoneBooking[];
  orderedBookEquip: ServicesZoneBooking[];
  orderedRoomLayout: ServicesZoneBooking[];
}) => {
  const assignedCatering = floorPlanForService.cateringMenuAssignments
    ?.find(cat => cat.zoneScheduleId === bookedEntryId)
    ?.cateringMenuIds.filter(
      item => !orderedCatering.some(cat => cat.serviceId === item.cateringMenuId)
    );

  const assignedBookEquipment = floorPlanForService.bookableEquipmentAssignments
    ?.find(eq => eq.zoneScheduleId === bookedEntryId)
    ?.bookableEquipmentIds.filter(
      item => !orderedBookEquip.some(cat => cat.serviceId === item.bookableEquipmentId)
    );

  const assignedRoomlayout = floorPlanForService.roomLayoutAssignments
    ?.find(layout => layout.zoneScheduleId === bookedEntryId)
    ?.roomLayoutIds.filter(
      item => !orderedRoomLayout.some(cat => cat.serviceId === item.roomLayoutId)
    );

  return { assignedCatering, assignedBookEquipment, assignedRoomlayout };
};

export const buildCommonServiceProps = ({
  found,
  bookingInventoryId,
  base,
  serviceType
}: {
  found: any;
  bookingInventoryId: number;
  base: any;
  serviceType: ServiceType | string;
}) => ({
  ...base,
  id: found?.id ?? 0,
  name: found?.name ?? "No Name",
  serviceId: found?.id ?? 0, // should be returned from BE
  serviceType,
  setUpTime: base.setUpTime ?? 15,
  bookingLeadTime: base.bookingLeadTime ?? 1,
  bookingId: bookingInventoryId,
  permissions: base.permissions,
  status: null,
  deliveryStatus: null,
  statusChangedDate: DateTime.now().toJSDate(),
  qty: 0,
  orderedQty: 0,
  availableQty: null,
  ordered: false
});

/**
 * @returns unordered but assigned service items
 */
export const unorderedServiceMap = ({
  bookingInventoryId,
  assignedCatering,
  assignedBookEquipment,
  assignedRoomlayout,
  remoteCateringMenu,
  remoteBookableEquipment,
  remoteRoomLayout
}: {
  bookingInventoryId: number;
  assignedCatering: CateringMenuIds[] | undefined;
  assignedBookEquipment: BookableEquipmentIds[] | undefined;
  assignedRoomlayout: RoomlayoutIds[] | undefined;
  remoteCateringMenu: RemoteCateringMenu[] | undefined;
  remoteBookableEquipment: IEquipmentInventory[] | undefined;
  remoteRoomLayout: RemoteRoomlayout[] | undefined;
}) => {
  let caterings: ServiceData[] = [];
  let bookEquipments: ServiceData[] = [];
  let layouts: ServiceData[] = [];

  if (
    !assignedCatering ||
    !assignedBookEquipment ||
    !assignedRoomlayout ||
    !remoteCateringMenu ||
    !remoteBookableEquipment ||
    !remoteRoomLayout
  )
    return { caterings, bookEquipments, layouts };

  caterings = assignedCatering
    .map(cat => {
      const found = remoteCateringMenu.find(menu => menu.id === cat.cateringMenuId);
      return {
        ...buildCommonServiceProps({
          found,
          bookingInventoryId,
          base: cat,
          serviceType: ServiceType.Catering
        }),
        pricePerUnit: found?.pricePerUnit ?? 0,
        picturePath: found?.picturePath ?? null
      };
    })
    .filter(data => data !== undefined) as ServiceData[];

  bookEquipments = assignedBookEquipment
    .map(equip => {
      const found = remoteBookableEquipment.find(eq => eq.id === equip.bookableEquipmentId);
      return {
        ...buildCommonServiceProps({
          found,
          bookingInventoryId,
          base: equip,
          serviceType: ServiceType.Bookable_Equipment
        }),
        picturePath: found?.picturePath ?? null
      };
    })
    .filter(data => data !== undefined) as ServiceData[];

  layouts = assignedRoomlayout
    .map(layout => {
      const found = remoteRoomLayout.find(item => item.id === layout.roomLayoutId);
      return {
        ...buildCommonServiceProps({
          found,
          bookingInventoryId,
          base: layout,
          serviceType: ServiceType.Room_Layout
        }),
        description: found?.description,
        arrangement: found?.arrangement,
        picturePaths: found?.picturePaths
      };
    })
    .filter(data => data !== undefined) as ServiceData[];

  return { caterings, bookEquipments, layouts };
};

export function mapAssignedServiceData({
  bookingInventoryId,
  floorPlanForService,
  serviceBookings,
  remoteCateringMenu,
  remoteBookableEquipment,
  remoteRoomLayout
}: {
  bookingInventoryId: number;
  floorPlanForService: IFloorPayload;
  serviceBookings: ServicesZoneBooking[];
  remoteCateringMenu: RemoteCateringMenu[] | undefined;
  remoteBookableEquipment: IEquipmentInventory[] | undefined;
  remoteRoomLayout: RemoteRoomlayout[] | undefined;
}) {
  let caterings: ServiceData[] = [];
  let bookEquipments: ServiceData[] = [];
  let layouts: ServiceData[] = [];

  const bookedEntry = floorPlanForService.zones.find(
    zone => bookingInventoryId === (zone.inventory?.id || zone.inventoryId)
  );

  if (!bookedEntry?.id) return { caterings, bookEquipments, layouts };

  const { orderedCatering, orderedBookEquip, orderedRoomLayout } =
    filterOrderByType(serviceBookings);

  const { assignedCatering, assignedBookEquipment, assignedRoomlayout } = filterAssignedService({
    floorPlanForService,
    bookedEntryId: bookedEntry.id,
    orderedCatering,
    orderedBookEquip,
    orderedRoomLayout
  });

  const unorderedServices = unorderedServiceMap({
    bookingInventoryId,
    assignedCatering,
    assignedBookEquipment,
    assignedRoomlayout,
    remoteCateringMenu,
    remoteBookableEquipment,
    remoteRoomLayout
  });

  // use unordered services
  caterings = unorderedServices.caterings;
  bookEquipments = unorderedServices.bookEquipments;
  layouts = unorderedServices.layouts;

  return { caterings, bookEquipments, layouts };
}

export function mapCateringData({
  serviceBookings,
  remoteCateringMenu,
  startDate,
  maxSetUpTime,
  setCateringData,
  isServiceAdminBypass = false,
  isEditable = true
}: {
  serviceBookings: ServicesZoneBooking[];
  remoteCateringMenu: RemoteCateringMenu[];
  startDate: string;
  maxSetUpTime: number;
  setCateringData: Dispatch<SetStateAction<ServiceData[]>>;
  isServiceAdminBypass?: boolean;
  isEditable?: boolean;
}) {
  const scheduledBookingFrom = DateTime.fromISO(startDate).toFormat("yyyy-MM-dd");
  const scheduledBookingStart = DateTime.fromISO(startDate).toFormat("HH:mm");

  const ordered: ServiceData[] = serviceBookings
    .map(data => {
      if (data.serviceType !== ServiceType.Catering) return undefined;

      const found = remoteCateringMenu.find(menu => menu.id === data.serviceId);

      const setUpTime = data.setUpTime ?? 15;
      const bookingLeadTime = data.bookingLeadTime ?? 1;

      const editable: boolean = isServiceItemOrderable({
        bookingFrom: scheduledBookingFrom,
        bookingStart: scheduledBookingStart,
        setUpTime,
        bookingLeadTime,
        maxSetupTime: maxSetUpTime,
        isServiceAdminBypass
      });

      return {
        ...data,
        name: found?.name ?? "No Name",
        pricePerUnit: found?.pricePerUnit ?? 0,
        picturePath: found?.picturePath ?? null,
        orderedQty: data.orderedQty,
        availableQty: data.availableQty,
        setUpTime,
        ordered: true,
        editable: isEditable && editable
      };
    })
    .filter(data => data !== undefined) as ServiceData[];

  setCateringData(ordered);
}

export function mapEquipmentData({
  serviceBookings,
  remoteBookableEquipment,
  startDate,
  maxSetUpTime,
  setEquipmentData,
  isServiceAdminBypass = false,
  isEditable = true
}: {
  serviceBookings: ServicesZoneBooking[];
  remoteBookableEquipment: IEquipmentInventory[];
  startDate: string;
  maxSetUpTime: number;
  setEquipmentData: Dispatch<SetStateAction<ServiceData[]>>;
  isServiceAdminBypass?: boolean;
  isEditable?: boolean;
}) {
  const scheduledBookingFrom = DateTime.fromISO(startDate).toFormat("yyyy-MM-dd");
  const scheduledBookingStart = DateTime.fromISO(startDate).toFormat("HH:mm");

  const modified: ServiceData[] = serviceBookings
    .map(data => {
      if (data.serviceType !== ServiceType.Bookable_Equipment) return undefined;

      const found = remoteBookableEquipment.find(eq => eq.id === data.serviceId);

      const setUpTime = data.setUpTime ?? 15;
      const bookingLeadTime = data.bookingLeadTime ?? 1;

      const editable = isServiceItemOrderable({
        bookingFrom: scheduledBookingFrom,
        bookingStart: scheduledBookingStart,
        setUpTime,
        bookingLeadTime,
        maxSetupTime: maxSetUpTime,
        isServiceAdminBypass
      });

      return {
        ...data,
        name: found?.name ?? "No Name",
        picturePath: found?.picturePath ?? null,
        ordered: true,
        editable: isEditable && editable
      };
    })
    .filter(data => data !== undefined) as ServiceData[];

  setEquipmentData(modified);
}

export function mapRoomLayoutData({
  serviceBookings,
  remoteRoomLayout,
  startDate,
  maxSetUpTime,
  setLayoutData,
  isServiceAdminBypass = false,
  isEditable = true
}: {
  serviceBookings: ServicesZoneBooking[];
  remoteRoomLayout: RemoteRoomlayout[];
  startDate: string;
  maxSetUpTime: number;
  setLayoutData: Dispatch<SetStateAction<ServiceData[]>>;
  isServiceAdminBypass?: boolean;
  isEditable?: boolean;
}) {
  const scheduledBookingFrom = DateTime.fromISO(startDate).toFormat("yyyy-MM-dd");
  const scheduledBookingStart = DateTime.fromISO(startDate).toFormat("HH:mm");

  const modified: ServiceData[] = serviceBookings
    .map(data => {
      if (data.serviceType !== ServiceType.Room_Layout) return undefined;

      const item = remoteRoomLayout.find(layout => layout.id === data.serviceId);

      const setUpTime = data.setUpTime ?? 15;
      const bookingLeadTime = data.bookingLeadTime ?? 1;

      const editable = isServiceItemOrderable({
        bookingFrom: scheduledBookingFrom,
        bookingStart: scheduledBookingStart,
        setUpTime,
        bookingLeadTime,
        maxSetupTime: maxSetUpTime,
        isServiceAdminBypass
      });

      return {
        ...data,
        name: item?.name,
        description: item?.description,
        arrangement: item?.arrangement,
        picturePaths: item?.picturePaths,
        ordered: true,
        editable: isEditable && editable
      };
    })
    .filter(data => data !== undefined) as ServiceData[];

  setLayoutData(modified);
}

export function updateUnorderedServiceData({
  serviceData,
  startDate,
  maxSetupTime,
  bookingUserInfo,
  updateAction,
  isServiceAdminBypass = false
}: {
  serviceData: { caterings: ServiceData[]; bookEquipments: ServiceData[]; layouts: ServiceData[] };
  startDate: string;
  maxSetupTime: number;
  bookingUserInfo: {
    userId: string;
    costCenterId: string;
    companyId: number;
  };
  updateAction: {
    setCateringData: Dispatch<SetStateAction<ServiceData[]>>;
    setEquipmentData: Dispatch<SetStateAction<ServiceData[]>>;
    setLayoutData: Dispatch<SetStateAction<ServiceData[]>>;
  };
  isServiceAdminBypass?: boolean;
}) {
  const { caterings, bookEquipments, layouts } = serviceData;
  const { setCateringData, setEquipmentData, setLayoutData } = updateAction;
  const scheduledBookingFrom = DateTime.fromISO(startDate).toFormat("yyyy-MM-dd");
  const scheduledBookingStart = DateTime.fromISO(startDate).toFormat("HH:mm");

  const orderableCaterings = caterings
    .filter(cat =>
      isServiceItemOrderable({
        bookingFrom: scheduledBookingFrom,
        bookingStart: scheduledBookingStart,
        setUpTime: cat.setUpTime ?? 15,
        bookingLeadTime: cat.bookingLeadTime ?? 1,
        maxSetupTime: maxSetupTime,
        isServiceAdminBypass
      })
    )
    .filter(item => {
      const isPermitted = checkPermission({
        permissions: item.permissions ?? undefined,
        userId: bookingUserInfo.userId,
        costCenterId: bookingUserInfo.costCenterId,
        companyId: bookingUserInfo.companyId
      });
      if (isPermitted) return item;
      return null;
    })
    .map(item => ({ ...item, editable: true }));

  const orderableBookEquipments = bookEquipments
    .filter(eq =>
      isServiceItemOrderable({
        bookingFrom: scheduledBookingFrom,
        bookingStart: scheduledBookingStart,
        setUpTime: eq.setUpTime ?? 15,
        bookingLeadTime: eq.bookingLeadTime ?? 1,
        maxSetupTime: maxSetupTime,
        isServiceAdminBypass
      })
    )
    .map(item => ({ ...item, editable: true }));

  const orderableLayouts = layouts
    .filter(layout =>
      isServiceItemOrderable({
        bookingFrom: scheduledBookingFrom,
        bookingStart: scheduledBookingStart,
        setUpTime: layout.setUpTime ?? 15,
        bookingLeadTime: layout.bookingLeadTime ?? 1,
        maxSetupTime: maxSetupTime,
        isServiceAdminBypass
      })
    )
    .map(item => ({ ...item, editable: true }));

  setCateringData(prev => [...prev, ...orderableCaterings]);
  setEquipmentData(prev => [...prev, ...orderableBookEquipments]);
  setLayoutData(prev => [...prev, ...orderableLayouts]);
}

export function storeUserInfoData({
  permissionContext,
  setSelectCostCenter
}: {
  permissionContext: PermissionContext | undefined;
  setSelectCostCenter: (center: { id: string; name: string }) => void;
}): void {
  if (!permissionContext) return;
  const { companyId, costCenterUnitId, costCenterUnitName } = permissionContext;
  if (!companyId || !costCenterUnitId || !costCenterUnitName) return;

  setSelectCostCenter({ id: costCenterUnitId, name: costCenterUnitName });
}
